import React, { useEffect, useState, useMemo, useRef } from 'react';
import StoreFinder from "../StoreFinder/StoreFinder";

interface Variant {
  id: number;
  title: string;
  option2: string;
}

interface Product {
  title: string;
  variants: Variant[];
}

interface Props {
  product: Product;
  selectedVariant?: number;
  onVariantChange?: (size: number) => void;
}

interface Store {
  id: string;
  name: string;
  address_street_1?: string;
  address_street_2?: string;
  city?: string;
  state?: string;
  postcode?: string | number;
  latitude?: number;
  longitude?: number;
  distance?: string | number;
  stock_status?: string;
  bg?: string;
  color?: string;
  phone_number?: string;
  store_email?: string;
  store_closing_hour?: string;
  sfs_enabled?: number;
  clickandcollect_enabled?: number;
  virtual_enabled?: number;
  deptstore_enabled?: number;

}

let unavailableSizes = [];

export const ProductFindInStoreModal: React.FC<Props> = ({ settings }) => {
  const inputRef = useRef(null);
  const storeFinderRef = useRef<{ searchStores: () => void }>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeSize, setActiveSize] = useState<string | null>(null);
  const [storeResults, setStoreResults] = useState<any>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const storesByLocation: Store[] = storeResults?.storesByLocation
    ? (Object.values(storeResults.storesByLocation)
        .flat()
        .filter((store) =>
          Boolean(store) &&
          (store as Store).stock_status === "In Stock" &&
          (
            (store as Store).clickandcollect_enabled === 1 ||
            (store as Store).sfs_enabled === 1 ||
            (store as Store).deptstore_enabled === 1
          )
        ) as Store[])
    : [];

  // console.log('storeResults', storesByLocation)

  const variantIds = useMemo(() => {
    if (!activeSize) return [];
    const activeVariant = settings?.product?.variants.find((variant) => variant.option2 === activeSize);
    return activeVariant ? [activeVariant.id] : [];
  }, [settings, activeSize]);

  const variantSizes = useMemo(() => {
    return settings?.product?.variants
      ?.map((variant) => variant.option2)
      .filter((size, index, self) => size && self.indexOf(size) === index) || [];
  }, [settings]);

  const toggleModal = () => {
    // setIsOpen((prev) => !prev);
    setIsOpen((prev) => {
      const isNowOpen = !prev;
      const pdpDetails = document.querySelector('.section-main-product .shopify-section-main-product-inner .pdp-details') as HTMLElement;

      if (pdpDetails) {
        if (isNowOpen) {
          pdpDetails.classList.add('z-10');
        } else {
          pdpDetails.classList.remove('z-10');
        }
      }

      return isNowOpen;
    });
  };

  const handleSizeSelect = (size: string) => {
    setActiveSize(size);
    setStoreResults({}); // Clear results before fetching
    setHasSearched(true);
    const input = document.querySelector(`input[name="Size-pdp-main"][value="${size}"]`) as HTMLInputElement;
    if (input) input.checked = true;

    setTimeout(() => {
      if (storeFinderRef.current?.searchStores) {
        // console.log('[FIS] search for size:', size);
        setIsLoading(true);
        storeFinderRef.current.searchStores();
      }
    }, 50);
  };

  const handleCustomAddToCartBtn = (store: Store) => {
    if (!activeSize) {
      alert("Please select a size first.");
      return;
    }
  
    const addToCartButton = document.querySelector('[data-add-to-cart]') as HTMLButtonElement | null;
  
    if (addToCartButton) {
      addToCartButton.click();
  
      // Trigger cart fetch update after short delay
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('liquid-ajax-cart:request-end'));
      }, 500);
    } else {
      console.error('Add to Cart button not found.');
    }
  };

  useEffect(() => {
    if (activeSize) setActiveSize(activeSize);

    const handleVariantChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { size } = customEvent.detail;
      setActiveSize(size);
    };

    window.addEventListener('variant:change', handleVariantChange);
    return () => window.removeEventListener('variant:change', handleVariantChange);
    setHasSearched(true);
  }, [activeSize]);

  useEffect(() => {
    document.body.classList.toggle('modal-open', isOpen);
    return () => document.body.classList.remove('modal-open');
  }, [isOpen]);

  useEffect(() => {
    function updateUnavailableForShipping(event: CustomEvent) {
      const unavailableForShipping = event.detail.unavailableForShipping || [];
      unavailableSizes = unavailableForShipping.map((id: string) => {
        const variant = settings?.product?.variants.find((v) => v.id == id);
        return variant ? variant.option2 : null;
      });
    }

    // @ts-ignore
    window.addEventListener('unavailableForShipping', updateUnavailableForShipping);

    return () => {
      // @ts-ignore
      window.removeEventListener('unavailableForShipping', updateUnavailableForShipping);
    };
  }, []);

  return (
    <div className='lg:mb-8'>
      <button
        className='w-auto px-0 font-medium text-black uppercase cursor-pointer bc'
        onClick={toggleModal}
      >
        Find in store
      </button>

        <div className={`${isOpen ? 'fixed inset-0 bg-[#72757e]/70 modal-overlay transition-opacity duration-300' : ''}`}>
          <div className={`fixed top-0 right-0 w-full max-w-md h-full bg-white lg:max-w-[594px] transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className='flex items-center justify-between p-4 lg:p-8'>
              <h5 className='text-[#1D1F22]'>Find in Store</h5>
              <button className='w-auto p-0' onClick={toggleModal}>✕</button>
            </div>

            <div className='p-4 overflow-y-auto max-h-[calc(100vh-200px)] hide-scrollbar lg:px-8 lg:py-0'>
              <h3 className='font-medium b2'>Select size</h3>
              <div className='flex flex-wrap gap-2 mt-4 mb-6 lg:gap-3.5'>
                {variantSizes.map((size) => {
                  return (
                    <label
                      key={size}
                      className={`px-5 py-2.5 cursor-pointer ${activeSize === size ? 'bg-[#00263e] text-white' : 'bg-[#ecebeb] text-[#00263e]'} b2 font-normal flex items-center gap-2`}
                    >
                      <input
                        type='radio'
                        value={size}
                        checked={activeSize === size}
                        onChange={() => handleSizeSelect(size)}
                        className='!hidden'
                      />
                      {size}
                    </label>
                  );
                })}
              </div>

              {activeSize && (
                <>
                  <StoreFinder
                    ref={storeFinderRef}
                    layout="default"
                    variantIds={variantIds}
                    onStoresFetched={(fetchedStores) => {
                      if (fetchedStores) {
                        setStoreResults(fetchedStores);
                      }
                      setIsLoading(false);
                    }}
                    onSearch={() => setHasSearched(true)}
                  />
                  {isLoading && inputRef.current && inputRef.current.value.trim() !== "" && (
                    <div className="mb-4 text-black b3">
                      Checking store availability...
                    </div>
                  )}
                </>
              )}

              {!isLoading && storesByLocation.length > 0 && (
                <ul className="p-0 space-y-3 list-none">
                  {storesByLocation.map((store, index) => (
                    <li key={index} className="grid grid-cols-1 gap-4 py-4 border-t lg:grid-cols-[3fr_1.2fr]">
                      <div className='order-2 lg:order-none'>
                        <h6 className="mb-2 text-black">{store.name}</h6>

                        <p className="b3 text-[#54565a]">
                          {store.address_street_1}
                        </p>

                        <p className="b3 text-[#54565a]">
                          {store.address_street_2}
                        </p>

                        <p className="b3 text-[#54565a]">
                          {store.city}, {store.state}
                        </p>

                        <div className="flex flex-col gap-2 mt-4">
                          <div className='flex flex-wrap gap-x-4 b3'>
                            {store.phone_number && (
                              <a
                                href={`tel:${store.phone_number.replace(/\s+/g, '')}`}
                                className="text-[#00263e] flex items-center hover:no-underline"
                              >
                                <i className="mr-2 text-black icon icon-phone"></i>
                                <span className="underline underline-offset-2">{store.phone_number}</span>
                              </a>
                            )}

                            {store.store_email && (
                              <a href={`mailto:${store.store_email}`} className='text-[#00263e] flex items-center hover:no-underline'>
                                <i className='mr-2 text-black icon icon-mail'></i>
                                <span className='underline underline-offset-2'>Email</span>
                              </a>
                            )}

                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#00263e] flex items-center hover:no-underline"
                            >
                              <i className="mr-2 text-black icon icon-location-wide"></i>
                              <span className="underline underline-offset-2">Get Directions</span>
                            </a>
                          </div>

                          <p className="b3 text-[#36573b] font-medium">
                            {store?.store_closing_hour}
                          </p>

                          <button
                            disabled={unavailableSizes.includes(activeSize) || store.clickandcollect_enabled === 0}
                            className="fis-atc text-[#00263e] border border-[#00263e] font-medium b2 cursor-pointer hover:bg-[#00263e] hover:text-white lg:w-max"
                            onClick={() => {
                              handleCustomAddToCartBtn(store);
                            }}
                          >
                            ADD TO CART
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between order-1 gap-2 lg:items-end lg:text-right lg:flex-col lg:justify-start lg:order-none">
                        <div className="flex items-center gap-1 b3 bg-[#f5f5f5] py-1 px-2">
                          {store.stock_status === "In Stock" ? (
                            <>
                              <i className='text-xl icon icon-check-circle'></i>
                              <span className='text-[#36573b]'>In stock</span>
                            </>
                          ) : (
                            <>
                              <i className='text-xl icon icon-cross-circle text-[#D12727]'></i>
                              <span className="text-[#D12727]">Out of stock</span>
                            </>
                          )}
                        </div>

                        {!unavailableSizes.includes(activeSize) && store.clickandcollect_enabled !== 0 &&
                          <span className='text-[#36573b] blabel'>
                            Click & Collect Available
                          </span>
                        }
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {!isLoading && hasSearched && storesByLocation.length === 0 && (
                <p className="mt-4 text-black b3">
                  No available stores found.
                </p>
              )}
            </div>
          </div>
        </div>
    </div>
  );
};

export default ProductFindInStoreModal;